// IE 2026 09 25 / ODOO-… (замечание Игоря 25.09: «если поиск не дал списка … поиск по позициям только на другом языке — по их названиям на других языках,
// не на том, на котором они отображены»): ИМЕНА ПУНКТА ПАНЕЛИ-МЕНЮ НА ДРУГИХ ЯЗЫКАХ ИНТЕРФЕЙСА — обратный словарь i18n.
// Панель показывает пункт на текущем языке; когда вся лестница поиска (фраза → части → раскладка → опечатки) не нашла ничего, панель ищет по
// названиям тех же пунктов на ДРУГИХ языках (en / uk / ru, кроме текущего). Откуда имена:
//   · команды (панель пользователя, ≡ виджета / таблицы, меню страницы) — строки t('English text'): английский ключ находится по показанному тексту
//     обратной картой словаря текущего языка (перевод → ключ; формы множественного числа — каждая; переводы с параметрами {n} / {title} — шаблоном),
//     имена на остальных языках — прямые словари messages/<lang>.json (грузятся лениво теми же чанками, что i18n/index.js: i18nNames_load);
//   · подписи, локализованные бэком (пометка _i18n + бэкап title_en / node.valueStr_en) — английское имя из бэкапа, остальные — словарь фронта по
//     этому ключу, иначе словарь бэка того языка (i18n/relocalize.dictGet: кэш IndexedDB, один запрос i18n_dict_get на язык за сеанс —
//     i18nNames_loadBackend, зовёт владелец панели при первом «пустом» результате; неудача помнится — повторных запросов нет);
//   · строки записей (страницы, виджеты, задачи, столы) — не здесь: бэк catalog_titles(langs='all') → кэш стора (mainStore.menuTitles_*).
// Чистая логика (без Vue-компонентов; юнит — handoff-files/menu-search-2026-09-25/i18nNames.test.mjs); реактивность — счётчик i18nNames_version
// (растёт при загрузке словарей: computed панели перечитывают имена)
import { shallowRef } from 'vue'
import { LANGUAGE_IDS, LANGUAGE_DEFAULT, getLanguage } from '@/i18n'

export const i18nNames_version = shallowRef(0)
// порядок языков ступени: английский (база всех ключей) первым, остальные — как в i18n
export const LANG_ORDER = [LANGUAGE_DEFAULT, ...LANGUAGE_IDS.filter(l => l !== LANGUAGE_DEFAULT)]
const _fe = {}       // lang → Map(ключ → перевод) — словари фронта (uk / ru)
const _feRev = {}    // lang → Map(перевод → ключ) — обратная карта
const _feTpl = {}    // lang → [{ re, key, names }] — переводы с параметрами {…}
const _be = {}       // lang → Map(ключ → перевод) — словари бэка (relocalize.dictGet)
const _beFailed = {} // lang → время неудачной загрузки словаря бэка (повторно в сеансе не просим)
let _feLoading = null
const _beLoading = {}
let _plural = {}     // lang → Intl.PluralRules

export function i18nNames_otherLangs(lang = getLanguage()) { return LANG_ORDER.filter(l => l !== lang) }
export function i18nNames_ready() { return LANG_ORDER.every(l => l === LANGUAGE_DEFAULT || !!_fe[l]) }
export function i18nNames_hasBackend(lang) { return !!_be[lang] }

// словарь языка (объект / Map «ключ → перевод»): ленивая загрузка кладёт сюда чанки messages/<lang>.json; юнит и пробы кладут словари сами
export function i18nNames_setDict(lang, dict) {
    _fe[lang] = dict instanceof Map ? dict : new Map(Object.entries(dict || {}))
    _feRev[lang] = null; _feTpl[lang] = null
    i18nNames_version.value++
}
export function i18nNames_setBackendDict(lang, dict) {
    _be[lang] = dict instanceof Map ? dict : new Map(Object.entries(dict || {}))
    i18nNames_version.value++
}
async function _loadFe(lang) {
    let m = null
    try {
        if (lang === 'uk') m = await import(/* webpackChunkName: "i18n-uk" */ '@/i18n/messages/uk.json')
        else if (lang === 'ru') m = await import(/* webpackChunkName: "i18n-ru" */ '@/i18n/messages/ru.json')
    } catch (e) { m = null }
    i18nNames_setDict(lang, (m && (m.default || m)) || {})
}
// словари фронта всех не английских языков (ленивые чанки; повторный вызов — тот же промис / готово)
export function i18nNames_load() {
    if (i18nNames_ready()) return Promise.resolve(true)
    if (_feLoading) return _feLoading
    _feLoading = (async () => {
        try { await Promise.all(LANGUAGE_IDS.filter(l => l !== LANGUAGE_DEFAULT && !_fe[l]).map(_loadFe)) } finally { _feLoading = null }
        return i18nNames_ready()
    })()
    return _feLoading
}
// IE 2026 09 27 / ODOO-341407 (заказ Игоря 27.09: английское имя в подсказке, utils/tipEnName.js): словарь ОДНОГО языка — обратной карте языка показа
// остальные языки не нужны (i18nNames_load грузит все); чанк тот же, что у i18n/index.js, — язык показа уже в кэше модулей
const _langLoading = {}
export function i18nNames_hasLang(lang = getLanguage()) { return lang === LANGUAGE_DEFAULT || !!_fe[lang] }
export function i18nNames_loadLang(lang = getLanguage()) {
    if (i18nNames_hasLang(lang)) return Promise.resolve(true)
    if (!_langLoading[lang]) _langLoading[lang] = _loadFe(lang).then(() => true, () => false).finally(() => { delete _langLoading[lang] })
    return _langLoading[lang]
}
// словари БЭКА других языков — для подписей, локализованных бэком (бэкап title_en есть, перевода в словарях фронта нет): relocalize.dictGet
// (память → IndexedDB по etag → бэк); ошибка — язык пропускается и в сеансе больше не просится (тихая деградация)
export function i18nNames_loadBackend(store, langs = i18nNames_otherLangs()) {
    const jobs = []
    for (const l of langs || []) {
        if (l === LANGUAGE_DEFAULT || _be[l] || _beFailed[l]) continue
        if (!_beLoading[l]) {
            _beLoading[l] = (async () => {
                try {
                    const mod = await import(/* webpackChunkName: "i18n-relocalize" */ '@/i18n/relocalize')
                    const entry = await mod.dictGet(store, l)
                    if (entry && entry.dict) i18nNames_setBackendDict(l, entry.dict)
                    else _beFailed[l] = Date.now()
                } catch (e) { _beFailed[l] = Date.now() }
                finally { delete _beLoading[l] }
            })()
        }
        jobs.push(_beLoading[l])
    }
    return Promise.all(jobs)
}

const _escRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
// перевод с параметрами → шаблон: «Згортання: {n}» → /^Згортання: (.+?)$/ + имена параметров
function _template(text, key) {
    const names = []
    const src = String(text).split(/(\{\w+\})/).map(part => {
        const m = /^\{(\w+)\}$/.exec(part)
        if (m) { names.push(m[1]); return '(.+?)' }
        return _escRe(part)
    }).join('')
    if (!names.length) return null
    try { return { re: new RegExp('^' + src + '$', 'i'), key, names } } catch (e) { return null }
}
function _rev(lang) {
    if (_feRev[lang]) return _feRev[lang]
    const rev = new Map(), tpl = []
    for (const [k, v] of (_fe[lang] || new Map())) {
        const forms = Array.isArray(v) ? v : [v]
        for (const f of forms) {
            if (typeof f !== 'string' || !f) continue
            if (!rev.has(f)) rev.set(f, k)
            const low = f.toLowerCase()
            if (!rev.has(low)) rev.set(low, k)
            if (f.includes('{')) { const t = _template(f, k); if (t) tpl.push(t) }
        }
    }
    _feRev[lang] = rev; _feTpl[lang] = tpl
    return rev
}
function _tplMatch(lang, text) {
    _rev(lang)
    for (const tp of (_feTpl[lang] || [])) {
        const m = tp.re.exec(text)
        if (!m) continue
        const params = {}
        // IE 2026 09 25 (доводка p193f): подставленное значение параметра — число / одно слово до 32 знаков; иначе это не перевод шаблона, а чужой текст с тем же
        // началом (замер dev: «Замовлення на закупівлю» ≈ шаблон «Замовлення {n}» давал мнимое английское имя «Order на закупівлю»)
        let sane = true
        tp.names.forEach((n, i) => { const v = String(m[i + 1] ?? ''); params[n] = v; if (!v || v.length > 32 || /\s/.test(v)) sane = false })
        if (!sane) continue
        return { key: tp.key, params }
    }
    return null
}
const _interpolate = (text, params) => params ? String(text).replace(/\{(\w+)\}/g, (m, k) => (params[k] === undefined || params[k] === null) ? m : String(params[k])) : text
// форма множественного числа перевода-массива [one, few, many] по числу параметра n (как i18n._pluralForm)
function _form(v, lang, params) {
    if (!Array.isArray(v)) return v
    const n = Number(params && params.n)
    let cat = 'other'
    if (!isNaN(n)) {
        try { _plural[lang] = _plural[lang] || new Intl.PluralRules({ en: 'en-US', uk: 'uk-UA', ru: 'ru-RU' }[lang] || 'en-US'); cat = _plural[lang].select(n) } catch (e) { cat = n === 1 ? 'one' : 'other' }
    }
    if (v.length >= 3) return v[{ one: 0, few: 1, many: 2, other: 2 }[cat] ?? 2] ?? v[v.length - 1]
    if (v.length === 2) return v[cat === 'one' ? 0 : 1]
    return v[0]
}
const _stripCtx = (key) => { const i = String(key).indexOf('|'); return i > 0 ? String(key).slice(0, i) : String(key) }   // ключ с контекстом 'Frozen|grid' → 'Frozen'
// перевод ключа на язык l: словарь фронта, затем словарь бэка; массив форм — по n
function _translate(key, l, params) {
    let v = _fe[l] ? _fe[l].get(key) : undefined
    if ((v === undefined || v === null || v === '') && _be[l]) v = _be[l].get(key)
    if (v === undefined || v === null || v === '') return ''
    v = _form(v, l, params)
    return (typeof v === 'string' && v) ? _interpolate(v, params) : ''
}

// имена ПОКАЗАННОГО текста на других языках: { en: 'Filters', ru: 'Фильтры' } — без языка показа и без совпадающих с показанным текстом;
// null — словарь текущего языка ещё не загружен либо ничего не известно. Ключ (английский): показ на en — сам текст; иначе обратная карта
// словаря языка показа (точно / без учёта регистра / шаблоном с параметрами), не нашлось — текст как есть (пункт показан без перевода)
export function i18nNames_of(text, lang = getLanguage()) {
    const s = String(text ?? '').trim()
    if (!s) return null
    let key = null, params = null
    if (lang === LANGUAGE_DEFAULT) key = s
    else {
        if (!_fe[lang]) return null
        const rev = _rev(lang)
        key = rev.get(s) ?? rev.get(s.toLowerCase()) ?? null
        if (key === null) { const m = _tplMatch(lang, s); if (m) { key = m.key; params = m.params } }
        if (key === null) key = s
    }
    return _namesByKey(key, params, s, lang)
}
function _namesByKey(key, params, shown, lang) {
    const out = {}
    const low = String(shown).trim().toLowerCase()
    for (const l of LANG_ORDER) {
        if (l === lang) continue
        const v = (l === LANGUAGE_DEFAULT) ? _interpolate(_stripCtx(key), params) : _translate(key, l, params)
        if (typeof v === 'string' && v && v.trim().toLowerCase() !== low) out[l] = v
    }
    return Object.keys(out).length ? out : null
}
// английский бэкап подписи, локализованной бэком (attrs._i18n_apply: пометка _i18n + '{key}_en'): title_en / node.valueStr_en / valueStr_en
export function i18nNames_enBackup(raw) {
    if (!raw || typeof raw !== 'object') return ''
    for (const v of [raw.title_en, raw.titleAction_en, raw.node && raw.node.valueStr_en, raw.valueStr_en, raw._row && raw._row.title_en, raw._row && raw._row.node && raw._row.node.valueStr_en]) {
        if (typeof v === 'string' && v.trim() && v !== '-') return v.trim()
    }
    return ''
}
// имена строки панели на других языках: бэкап бэка (английский ключ точнее обратной карты) + словари по нему, иначе по показанному тексту
export function i18nNames_ofRow(raw, shown, lang = getLanguage()) {
    const en = i18nNames_enBackup(raw)
    const byShown = i18nNames_of(shown, lang)
    if (!en) return byShown
    const byEn = _namesByKey(en, null, shown, lang)
    if (!byEn) return byShown
    if (!byShown) return byEn
    return { ...byShown, ...byEn }   // бэкап главнее
}
// имена строки-ЗАПИСИ (страница / виджет / задача / стол) — только бэкап бэка title_en и словари по нему: показанный текст записи — не UI-строка, обратная карта
// по нему давала мнимые имена (IE 2026 09 25, доводка p193f); null — бэкапа нет (имена записи на других языках — из кэша стора catalog_titles)
export function i18nNames_ofRowBackup(raw, shown, lang = getLanguage()) {
    const en = i18nNames_enBackup(raw)
    return en ? _namesByKey(en, null, shown, lang) : null
}
// служебное (пробы / юнит): сброс словарей
export function i18nNames_reset() {
    for (const o of [_fe, _feRev, _feTpl, _be, _beFailed, _beLoading]) for (const k of Object.keys(o)) delete o[k]
    _plural = {}
    i18nNames_version.value++
}
